import { ref, get, set, remove } from 'firebase/database';
import { database } from '../firebase/firebase';
import { generateId } from './helpers';

// Cloned events get their own id prefix so they are trivial to spot in the
// admin list — and trivial to grep for when it is time to clean the DB up.
export const CLONE_ID_PREFIX = 'ev_test_';

export const isClonedEvent = (eventId: string): boolean => eventId.startsWith(CLONE_ID_PREFIX);

// Same window ExpenseForm / SponsorForm hand out on create.
const EDIT_WINDOW_MS = 5 * 60 * 1000;

// `${from}_to_${to}`, mirroring settlementKeyOf in SuggestedPaymentsTab
const SETTLEMENT_KEY_SEP = '_to_';

export interface CloneEventOptions {
    // uid that should own the clone — the admin performing the clone.
    newOwnerId: string;
    // Rewrite every reference to the source owner so it points at newOwnerId.
    // The participant count stays the same, so every split — and therefore every
    // settlement number — is identical to the source, while the admin gets the
    // owner seat (and the collector seat, if the owner held it).
    takeOwnerSeat?: boolean;
    namePrefix?: string;
}

export interface CloneEventResult {
    eventId: string;
    name: string;
    ownerSeatTaken: boolean;
    participantCount: number;
    expenseCount: number;
    warnings: string[];
}

type AnyRecord = Record<string, any>;

const deepCopy = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const remapKeys = (source: AnyRecord, remap: (id: string) => string): AnyRecord => {
    const out: AnyRecord = {};
    Object.entries(source).forEach(([key, value]) => {
        out[remap(key)] = value;
    });
    return out;
};

/**
 * Copy an event into a brand new one under a fresh id.
 *
 * The source event is only ever READ — every write targets the new id (plus the
 * cloning admin's own userEvents index), so a clone can never corrupt live data.
 */
export const cloneEvent = async (
    sourceEventId: string,
    { newOwnerId, takeOwnerSeat = true, namePrefix = '[TEST]' }: CloneEventOptions
): Promise<CloneEventResult> => {
    const warnings: string[] = [];

    const [eventSnapshot, expensesSnapshot, usersSnapshot] = await Promise.all([
        get(ref(database, `events/${sourceEventId}`)),
        get(ref(database, `eventExpenses/${sourceEventId}`)),
        get(ref(database, `eventUsers/${sourceEventId}`)),
    ]);

    if (!eventSnapshot.exists()) {
        throw new Error(`Event ${sourceEventId} not found`);
    }

    const source = deepCopy(eventSnapshot.val()) as AnyRecord;
    let expenses = expensesSnapshot.exists() ? deepCopy(expensesSnapshot.val()) as AnyRecord : {};
    let eventUsers = usersSnapshot.exists() ? deepCopy(usersSnapshot.val()) as AnyRecord : {};

    const meta = (source.meta || {}) as AnyRecord;
    const sourceOwnerId: string | undefined = meta.createdBy;
    let participants = (meta.participants || {}) as AnyRecord;

    // --- Decide whether the admin can take over the source owner's seat ---
    let remap = (id: string): string => id;
    let ownerSeatTaken = false;

    if (!sourceOwnerId) {
        warnings.push('Source event has no meta.createdBy — owner seat left empty.');
    } else if (sourceOwnerId === newOwnerId) {
        ownerSeatTaken = true;
    } else if (takeOwnerSeat) {
        if (participants[newOwnerId]) {
            // Remapping would merge two distinct participants into one and change
            // every split. Keep the ids untouched instead.
            warnings.push(
                'You are already a participant of the source event, so participant ids were kept as-is ' +
                '(otherwise two participants would merge and the splits would change).'
            );
        } else {
            remap = (id: string) => (id === sourceOwnerId ? newOwnerId : id);
            ownerSeatTaken = true;
        }
    }

    // --- meta ---
    if (ownerSeatTaken && remap(sourceOwnerId!) === newOwnerId) {
        participants = remapKeys(participants, remap);
    }

    const clonedMeta: AnyRecord = {
        ...meta,
        name: namePrefix ? `${namePrefix} ${meta.name || 'Unnamed Event'}` : (meta.name || 'Unnamed Event'),
        createdBy: newOwnerId,
        createdAt: Date.now(),
        participants,
    };

    // Keyed by participant like `participants` — an owner's ratio left on the old
    // id would quietly put them back on a full share and change every split.
    if (meta.shareRatios) {
        clonedMeta.shareRatios = remapKeys(meta.shareRatios, remap);
    }

    // --- expenses ---
    const now = Date.now();
    expenses = Object.fromEntries(
        Object.entries(expenses).map(([expenseId, raw]) => {
            const expense = { ...(raw as AnyRecord) };

            // `payer` is the literal 'sponsor' on sponsor records — remap leaves it alone.
            (['payer', 'createdBy', 'lastModifiedBy', 'sponsorHolder', 'sponsorPayer'] as const).forEach(field => {
                if (typeof expense[field] === 'string') {
                    expense[field] = remap(expense[field]);
                }
            });

            if (Array.isArray(expense.splitBetween)) {
                expense.splitBetween = expense.splitBetween.map((id: string) => remap(id));
            }

            // Reopen the edit window so the clone is actually editable in the UI.
            expense.editableUntil = now + EDIT_WINDOW_MS;

            return [expenseId, expense];
        })
    );

    // --- settlementConfig ---
    const clonedRoot: AnyRecord = { meta: clonedMeta };

    if (source.settlementConfig) {
        const config = { ...(source.settlementConfig as AnyRecord) };
        if (typeof config.collectorId === 'string') {
            config.collectorId = remap(config.collectorId);
        }
        clonedRoot.settlementConfig = config;
    }

    // --- settlementStatus (keys encode the from/to pair) ---
    if (source.settlementStatus) {
        const status: AnyRecord = {};
        Object.entries(source.settlementStatus as AnyRecord).forEach(([key, raw]) => {
            const entry = { ...(raw as AnyRecord) };
            if (typeof entry.from === 'string') entry.from = remap(entry.from);
            if (typeof entry.to === 'string') entry.to = remap(entry.to);

            const separatorIndex = key.indexOf(SETTLEMENT_KEY_SEP);
            const newKey = separatorIndex === -1
                ? key
                : remap(key.slice(0, separatorIndex))
                    + SETTLEMENT_KEY_SEP
                    + remap(key.slice(separatorIndex + SETTLEMENT_KEY_SEP.length));

            status[newKey] = entry;
        });
        clonedRoot.settlementStatus = status;
    }

    // --- eventUsers ---
    eventUsers = Object.fromEntries(
        Object.entries(remapKeys(eventUsers, remap)).map(([userId, raw]) => {
            const profile = { ...(raw as AnyRecord) };
            if (typeof profile.userId === 'string') profile.userId = remap(profile.userId);
            return [userId, profile];
        })
    );

    // --- Write the clone. Nothing above touched the source paths. ---
    const newEventId = `${CLONE_ID_PREFIX}${generateId()}`;

    await set(ref(database, `events/${newEventId}`), clonedRoot);

    if (Object.keys(expenses).length > 0) {
        await set(ref(database, `eventExpenses/${newEventId}`), expenses);
    }
    if (Object.keys(eventUsers).length > 0) {
        await set(ref(database, `eventUsers/${newEventId}`), eventUsers);
    }

    // Clones always start open, so the admin can edit freely.
    await set(ref(database, `eventList/${newEventId}`), true);
    await set(ref(database, `userEvents/${newOwnerId}/${newEventId}`), true);

    return {
        eventId: newEventId,
        name: clonedMeta.name,
        ownerSeatTaken,
        participantCount: Object.keys(participants).length,
        expenseCount: Object.keys(expenses).length,
        warnings,
    };
};

/**
 * Remove an event from every location it was written to.
 *
 * `userEvents/<owner>` is only writable by that owner, so for a foreign event
 * that one leg is expected to fail — its path is returned instead of throwing,
 * and the rest of the cleanup still goes through.
 */
export const purgeEvent = async (eventId: string): Promise<string[]> => {
    const eventSnapshot = await get(ref(database, `events/${eventId}`));
    const ownerId: string | undefined = eventSnapshot.exists()
        ? eventSnapshot.val()?.meta?.createdBy
        : undefined;

    const paths = [
        `events/${eventId}`,
        `eventExpenses/${eventId}`,
        `eventUsers/${eventId}`,
        `eventList/${eventId}`,
        ...(ownerId ? [`userEvents/${ownerId}/${eventId}`] : []),
    ];

    const results = await Promise.allSettled(paths.map(path => remove(ref(database, path))));

    return paths.filter((_, index) => results[index].status === 'rejected');
};
